import type { Availability, Meeting, MeetingRequest } from "../../services/meetingsService";
import { byText, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * Parent–teacher meetings: a teacher schedules one (or answers a parent's
 * request with one), the parent confirms, and the teacher records what was
 * agreed. What can be done here mirrors apps/meetings/views.py.
 */

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
export const time = (value?: string | null) => (value ? value.slice(0, 5) : "");
export const weekday = (value?: string | null) => {
  if (!value) return "";
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
};
export const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
/** 0 = Monday, as the server stores a teacher's weekly times. */
export const dayIndex = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return (new Date(y, m - 1, d).getDay() + 6) % 7;
};

type Pairs = [string, string][];
export const URGENCIES: Pairs = [["low", "Low"], ["medium", "Medium"], ["high", "High"], ["urgent", "Urgent"]];
export const labelOf = (pairs: Pairs, value?: string | null) => pairs.find(([key]) => key === value)?.[1] ?? (value || "—");
export const URGENCY_TONE: Record<string, string> = { low: "text-gray-600", medium: "text-gray-900", high: "font-semibold text-amber-800", urgent: "font-semibold text-red-700" };

type Tone = { label: string; dot: string };
const tone = (label: string, dot: string): Tone => ({ label, dot });

export const whenOf = (row: Pick<Meeting, "scheduled_date" | "scheduled_time" | "duration_minutes">) =>
  `${weekday(row.scheduled_date)} · ${time(row.scheduled_time)} (${row.duration_minutes} min)`;

const live = (row: Pick<Meeting, "status">) => row.status === "scheduled" || row.status === "confirmed";

export function meetingStatus(row: Pick<Meeting, "status" | "scheduled_date">, today: string): Tone {
  if (live(row) && row.scheduled_date < today) return tone("Outcome to record", "bg-red-500");
  switch (row.status) {
    case "scheduled": return tone("Awaiting parent", "bg-amber-500");
    case "confirmed": return row.scheduled_date === today ? tone("Today", "bg-sky-500") : tone("Confirmed", "bg-emerald-500");
    case "completed": return tone("Held", "bg-emerald-600");
    case "no_show": return tone("Did not attend", "bg-red-400");
    case "cancelled": return tone("Cancelled", "bg-gray-400");
    default: return tone("In progress", "bg-sky-500");
  }
}

/** A completed meeting whose follow-up date has come, with no later meeting booked between the two. */
export function followUpDue(row: Meeting, all: Meeting[], today: string) {
  if (row.status !== "completed" || !row.follow_up_required || !row.follow_up_date || row.follow_up_date > today) return false;
  return !all.some((other) => other.student === row.student && other.teacher === row.teacher && other.scheduled_date > row.scheduled_date && other.status !== "cancelled");
}

/** What a person can do with a meeting now. `staff` means the teacher or leadership. */
export function meetingActions(row: Meeting, staff: boolean, today: string) {
  const due = live(row) && row.scheduled_date <= today;
  return {
    confirm: !staff && row.status === "scheduled" && row.scheduled_date >= today,
    cancel: live(row),
    reschedule: staff && live(row),
    noShow: staff && due,
    complete: staff && due,
    rate: !staff && row.status === "completed" && !row.my_rating,
    followUp: staff && row.status === "completed",
  };
}

// ---- Requests ----------------------------------------------------------------------------

export function requestStatus(row: Pick<MeetingRequest, "status">): Tone {
  return ({ pending: tone("Waiting for the teacher", "bg-amber-500"), approved: tone("Meeting set", "bg-emerald-500"), rejected: tone("Declined", "bg-red-400"), cancelled: tone("Withdrawn", "bg-gray-400") } as Record<string, Tone>)[row.status];
}
export const preferredTimes = (row: Pick<MeetingRequest, "preferred_dates" | "preferred_times">) =>
  row.preferred_dates.map((date, index) => `${weekday(date)} ${time(row.preferred_times[index])}`).join("; ");

/** A preferred time a teacher has not said they are free for. */
export function outsideAvailability(slots: Availability[], teacher: number, date: string, at: string) {
  const mine = slots.filter((slot) => slot.teacher === teacher && slot.is_available);
  if (!mine.length || !date || !at) return false;
  const weekdayIndex = dayIndex(date);
  return !mine.some((slot) => slot.day_of_week === weekdayIndex && time(slot.start_time) <= at && time(slot.end_time) > at);
}

// ---- Registers -----------------------------------------------------------------------------

const urgencyRank: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

export function meetingConfig(today: string, staff: boolean, all: Meeting[]): RegisterConfig<Meeting> {
  return {
    search: (row) => `${row.student_name} ${row.student_number ?? ""} ${row.class_name ?? ""} ${row.parent_name} ${row.teacher_name} ${row.title} ${row.meeting_type_name}`,
    views: [
      { key: "upcoming", label: "Coming up", match: (row) => live(row) && row.scheduled_date >= today },
      { key: "today", label: "Today", match: (row) => live(row) && row.scheduled_date === today },
      { key: "confirm", label: staff ? "Awaiting parent" : "To confirm", match: (row) => row.status === "scheduled" && row.scheduled_date >= today },
      ...(staff ? [{ key: "outcome", label: "Outcome to record", match: (row: Meeting) => live(row) && row.scheduled_date < today }] : []),
      { key: "follow", label: "Follow-up due", match: (row) => followUpDue(row, all, today) },
      { key: "held", label: "Held", match: (row) => row.status === "completed" },
      { key: "closed", label: "Cancelled or missed", match: (row) => row.status === "cancelled" || row.status === "no_show" },
      { key: "all", label: "All" },
    ],
    date: { label: "Day", value: (row) => row.scheduled_date },
    category: { label: "Kind", value: (row) => row.meeting_type_name },
    sorts: [
      { key: "soonest", label: "Soonest first", compare: byText((row) => `${row.scheduled_date}${row.scheduled_time}`) },
      { key: "latest", label: "Latest first", compare: reversed(byText((row) => `${row.scheduled_date}${row.scheduled_time}`)) },
      { key: "learner", label: "Learner A–Z", compare: byText((row) => row.student_name) },
    ],
  };
}

export const requestConfig: RegisterConfig<MeetingRequest> = {
  search: (row) => `${row.student_name} ${row.requested_by_name} ${row.teacher_name} ${row.meeting_type_name} ${row.reason}`,
  views: [
    { key: "pending", label: "Waiting", match: (row) => row.status === "pending" },
    { key: "approved", label: "Meeting set", match: (row) => row.status === "approved" },
    { key: "closed", label: "Declined or withdrawn", match: (row) => row.status === "rejected" || row.status === "cancelled" },
    { key: "all", label: "All" },
  ],
  date: { label: "Asked", value: (row) => row.created_at },
  category: { label: "Urgency", value: (row) => labelOf(URGENCIES, row.urgency) },
  sorts: [
    { key: "urgent", label: "Most urgent first", compare: (a, b) => urgencyRank[a.urgency] - urgencyRank[b.urgency] || a.created_at.localeCompare(b.created_at) },
    { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.created_at)) },
  ],
};

export const availabilityConfig: RegisterConfig<Availability> = {
  search: (row) => `${row.teacher_name} ${DAYS[row.day_of_week]} ${row.reason ?? ""}`,
  views: [{ key: "all", label: "All" }],
  category: { label: "Day", value: (row) => DAYS[row.day_of_week], inRowOrder: true },
  sorts: [
    { key: "day", label: "By day", compare: (a, b) => a.day_of_week - b.day_of_week || a.start_time.localeCompare(b.start_time) || a.teacher_name.localeCompare(b.teacher_name) },
    { key: "teacher", label: "By teacher", compare: (a, b) => a.teacher_name.localeCompare(b.teacher_name) || a.day_of_week - b.day_of_week },
  ],
};
